import React, { createContext, useContext } from "react";
import { useAnalyticsChatSSE } from "../hooks/useAnalyticsChatSSE";
import { ChatMessage } from "../types";
import type { StoredChat } from "../hooks/useAnalyticsChatSSE";

interface AnalyticsChatContextValue {
  messages:     ChatMessage[];
  isRunning:    boolean;
  sendQuery:    (text: string) => void;
  /** Reword an earlier question and re-run it. Everything from that question
   *  downwards is dropped, on the client and on the server. */
  editQuery:    (userMessageId: string, text: string) => void;
  /** Refill an old answer's table by re-running its stored SQL. Rows are never
   *  persisted, so a reopened chat has no table until this is called. */
  rerunTurn:    (assistantMessageId: string) => void;
  /** Fetches the server-side chat list. Called by the copilot screen, not on
   *  mount: this provider wraps every authenticated page. */
  loadSessions: () => void | Promise<void>;
  reset:        () => void;
  chats:        StoredChat[];
  activeChatId: string | null;
  /** Opens a conversation. Transcripts listed by the server are fetched
   *  the first time their chat is opened, so this is async. */
  selectChat:   (id: string) => void | Promise<void>;
  deleteChat:   (id: string) => void;
}

const AnalyticsChatContext = createContext<AnalyticsChatContextValue | null>(null);

export const AnalyticsChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const chat = useAnalyticsChatSSE();
  return (
    <AnalyticsChatContext.Provider value={chat}>
      {children}
    </AnalyticsChatContext.Provider>
  );
};

export function useAnalyticsChat(): AnalyticsChatContextValue {
  const ctx = useContext(AnalyticsChatContext);
  if (!ctx) throw new Error("useAnalyticsChat must be used inside AnalyticsChatProvider");
  return ctx;
}
